import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Stack, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, FormControlLabel, Switch, FormHelperText } from "@mui/material";
import { PayerType } from '../../../../services/ConfigurationService/types/payerType';
import { useGetPayerGroupsQuery, useUpsertPayerMutation } from '../../../../services/ConfigurationService';
import { useGetCalcTypesQuery } from '../../../../services/ContractService';
import { useLocation } from 'react-router-dom';

interface EditPayerDialogProps {
  payer: PayerType;
  trigger: React.ReactNode;
}

export function EditPayerDialog({ payer, trigger }: EditPayerDialogProps) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const companyFromUrl = query.get('company') || payer.company || '';
  const [upsertPayer] = useUpsertPayerMutation();
  const [open, setOpen] = React.useState(false);
  const { data: calcTypes = [] } = useGetCalcTypesQuery();
  const { data: payerGroups = [], isLoading: payerGroupsLoading } = useGetPayerGroupsQuery();
  const [form, setForm] = React.useState<PayerType>({ ...payer, company: companyFromUrl, id: payer.id ?? '', payerGroup: payer.payerGroup ?? '' });
  const [payerGroupError, setPayerGroupError] = React.useState(false);

  const buildCompositePayerId = (company: string, centerId: string, payerCode: string): string => {
    const normalizedCompany = company.trim();
    const normalizedCenter = centerId.trim();
    const normalizedPayerCode = payerCode.trim();

    if (!normalizedCompany)
      return `${normalizedCenter}-${normalizedPayerCode}`;

    const expectedPrefix = `${normalizedCompany}-`;
    const centerWithoutCompany = normalizedCenter.toUpperCase().startsWith(expectedPrefix.toUpperCase())
      ? normalizedCenter.substring(expectedPrefix.length)
      : normalizedCenter;

    return `${normalizedCompany}-${centerWithoutCompany}-${normalizedPayerCode}`;
  };

  React.useEffect(() => {
    setForm({ ...payer, company: companyFromUrl, id: payer.id ?? '', payerGroup: payer.payerGroup ?? '' });
    setPayerGroupError(false);
  }, [payer, companyFromUrl]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value, company: companyFromUrl }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value, company: companyFromUrl }));
  };

  const handleEvaEnabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setForm(f => ({ ...f, evaEnabled: checked, company: companyFromUrl }));
    if (!checked)
      setPayerGroupError(false);
  };

  const handleSave = async () => {
    if (form.evaEnabled && !form.payerGroup.trim())
    {
      setPayerGroupError(true);
      return;
    }

    setPayerGroupError(false);

    // Set id as centerId-payerCode before saving
    const newForm = {
      ...form,
      id: buildCompositePayerId(companyFromUrl, form.centerId, form.payerCode),
      company: companyFromUrl,
    };
    await upsertPayer(newForm);
    setOpen(false);
  };

  return (
    <>
      <span onClick={() => setOpen(true)} style={{ cursor: 'pointer' }}>{trigger}</span>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Payer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Payer Code" name="payerCode" value={form.payerCode} onChange={handleChange} fullWidth />
            <TextField label="Payer Name" name="payerName" value={form.payerName} onChange={handleChange} fullWidth />
            <TextField label="System" name="system" value={form.system} onChange={handleChange} fullWidth />
            <FormControl fullWidth>
              <InputLabel>Financial Class</InputLabel>
              <Select
                name="financialClass"
                value={form.financialClass}
                onChange={handleSelectChange}
                label="Financial Class"
              >
                {calcTypes.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Portal" name="portal" value={form.portal} onChange={handleChange} fullWidth />
            <TextField label="Phone" name="phone" value={form.phone} onChange={handleChange} fullWidth />
            <TextField label="ECS Payer ID" name="ecsPayerId" value={form.ecsPayerId} onChange={handleChange} fullWidth />
            <TextField label="Waystar Auth Payer" name="waystarAuthPayer" value={form.waystarAuthPayer} onChange={handleChange} fullWidth />
            <FormControlLabel
              control={<Switch checked={form.evaEnabled} onChange={handleEvaEnabledChange} color="primary" />}
              label="EVA Enabled"
            />
            <FormControl fullWidth error={payerGroupError}>
              <InputLabel>Payer Group</InputLabel>
              <Select
                name="payerGroup"
                value={form.payerGroup}
                onChange={handleSelectChange}
                label="Payer Group"
                disabled={!form.evaEnabled || payerGroupsLoading}
              >
                {payerGroups.map((group) => (
                  <MenuItem key={group.value} value={group.value}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
              {payerGroupError ? <FormHelperText>Payer Group is required when EVA is enabled.</FormHelperText> : null}
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


